import classnames from 'classnames';
import React from 'react';

import {IconButton, Popover} from '@albert-io/atomic';
import {useHoverPopover} from 'lib/hooks/useHoverPopover';

import {useAnnotationModeStore} from './AnnotationModeStore';
import AnnotationNotePeek from './AnnotationNotePeek';
import {ANNOTATION_SHIELD_EXEMPT_PROPS} from './annotationFreeze';
import {HIGHLIGHT_BUTTON_CLASS} from './annotationHighlightStyles';
import type {AnnotationNoteLine} from './annotationNoteLines';

interface AnnotationNoteIndicatorProps {
  line: AnnotationNoteLine;
  /**
   * What `line.center` measures: the middle of a line box (the default, which pulls the
   * button up half its height) or the top edge of a marked element, which it hangs from.
   */
  align?: 'center' | 'top';
}

/**
 * The margin button for one line's notes: hover/focus peeks at them, activating
 * opens the notes panel, or toggles the peek where there is no panel. `top` is
 * inline because it is measured from rendered text. It takes no z-index, so the
 * pane's sticky chrome scrolls over it as it does the text.
 */
export default function AnnotationNoteIndicator({
  line,
  align = 'center'
}: AnnotationNoteIndicatorProps) {
  const noteCount = line.annotations.length;
  const label = noteCount === 1 ? 'Show 1 note' : `Show ${noteCount} notes`;
  const {isOpen, triggerRef, triggerProps, contentProps} = useHoverPopover();
  const isNotesPanelOpen = useAnnotationModeStore((state) => state.isNotesPanelOpen);
  const isReadOnly = useAnnotationModeStore((state) => state.isReadOnly);
  const openNotesPanel = useAnnotationModeStore((state) => state.openNotesPanel);

  return (
    <>
      <IconButton
        {...triggerProps}
        // Not the hook's toggle: every indicator opens the one panel, so one line's
        // button could close another's notes. Read-only has no panel, so the toggle stays.
        onClick={isReadOnly ? triggerProps.onClick : openNotesPanel}
        aria-disabled={isNotesPanelOpen}
        icon='sticky-note'
        iconStyle='regular'
        label={label}
        size='xs'
        className={classnames(
          'right-x0-half absolute',
          align === 'center' && '-translate-y-1/2',
          HIGHLIGHT_BUTTON_CLASS,
          {'cursor-default': isNotesPanelOpen}
        )}
        style={{top: line.center}}
        data-testid='annotation-note-indicator'
        {...ANNOTATION_SHIELD_EXEMPT_PROPS}
      />
      {isOpen && (
        // Opens inward, since the trigger sits against the pane's right edge.
        <Popover
          targetRef={triggerRef}
          expanded
          position='left-start'
          shadow='default'
          paddingLevel={0}
        >
          <AnnotationNotePeek line={line} triggerRef={triggerRef} contentProps={contentProps} />
        </Popover>
      )}
    </>
  );
}
